import { Component, h, State, Listen } from '@stencil/core';

interface PopupState {
  id: string;
  name: string; // name of the popup element tag
  props?: { [key: string]: unknown };
}

@Component({
  tag: 'popup-state-manager',
  shadow: false,
})
export class PopupStateManager {
  @State() popups: PopupState[] = [];

  @Listen('open-popup', { target: 'window' })
  handleOpenPopup(event: CustomEvent) {
    const { id, name, ...props } = event.detail;
    this.popups = [...this.popups, { id, name, props }];
  }

  @Listen('close-popup', { target: 'window' })
  handleClosePopup(event: CustomEvent<string>) {
    const id = event.detail;
    this.popups = this.popups.filter(popup => popup.id !== id);
  }

  private getPopupConfig(name: string, props?: { [key: string]: unknown }) {
    // Remove 'popup-' prefix if it exists
    const nameWithoutPrefix = name.startsWith('popup-') ? name.substring(6) : name;

    // Default configuration
    const config = {
      contentTag: name,
      containerTag: name,
      type: props?.type || 'type-1',
    };

    // Special handling for modal windows
    if (nameWithoutPrefix.includes('modal')) {
      config.containerTag = `popup-modal-container-${config.type}`;
    }

    // Here we can add more special cases for other types of popups
    // Example:
    // if (nameWithoutPrefix.includes('banner')) {
    //   config.containerTag = `popup-banner-container-${config.type}`;
    // }
    // if (nameWithoutPrefix.includes('cookie')) {
    //   config.containerTag = `popup-cookie-container-${config.type}`;
    // }

    return config;
  }

  private renderPopup(popup: PopupState) {
    const { containerTag, contentTag } = this.getPopupConfig(popup.name, popup.props);

    // Ensure popupId is always present in props and is not overwritten by undefined
    const allProps = { popupId: popup.id, ...popup.props };

    // Convert tag names to proper case for JSX
    const ContainerTag = containerTag;
    const ContentTag = contentTag;

    // If container and content tags are the same, render without wrapper
    if (containerTag === contentTag) {
      return <ContentTag key={popup.id} {...allProps} />;
    }

    // Otherwise, render content inside container
    return (
      <ContainerTag key={popup.id} {...allProps}>
        <ContentTag {...allProps} />
      </ContainerTag>
    );
  }

  render() {
    return <div class="popup-state-manager">{this.popups.map(popup => this.renderPopup(popup))}</div>;
  }
}
